// @/components/reusable/nav-list.jsx
// Navigation list components for sidebar navigation
// Snappy spring animations with 150ms hover transitions
// Supports layout variants via sidebar-theme.js config

import React from "react";
import { motion } from "motion/react";
import { Link } from "gatsby";
import clsx from "clsx";
import { useLayout } from "@/components/layout/main";
import { getTheme } from "@/config/sidebar-theme";

// ============================================================================
// ANIMATION CONFIG
// ============================================================================

const springConfig = {
  type: "spring",
  damping: 25,
  stiffness: 300,
  mass: 0.5,
};

// ============================================================================
// COMPONENTS
// ============================================================================

/**
 * NavList - Container for navigation sections
 */
export function NavList({ children, className }) {
  return <nav className={clsx("flex flex-col", className)}>{children}</nav>;
}

/**
 * NavSection - Groups navigation items with optional heading
 */
export function NavSection({ children, className }) {
  return <div className={clsx("mb-2", className)}>{children}</div>;
}

/**
 * NavHeading - Section heading (hidden when collapsed)
 */
export function NavHeading({ children, isExpanded = true, className }) {
  const { layout = "default" } = useLayout();
  const theme = getTheme(layout);

  if (!isExpanded) return null;

  return (
    <div
      className={clsx(
        "px-3 text-[10px] font-fono font-semibold uppercase tracking-wide",
        theme.nav.heading,
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * NavItem - Individual navigation link
 * Supports expanded/collapsed modes, active state, disabled state, and badges
 * When collapsed, badges are positioned at top-right of the icon
 */
export function NavItem({
  to,
  icon,
  label,
  isExpanded = true,
  active = false,
  disabled = false,
  badge = null,
  external = false,
  className,
}) {
  const { layout = "default" } = useLayout();
  const theme = getTheme(layout);

  const baseClasses = clsx(
    "flex items-center w-full rounded-xl",
    isExpanded ? "gap-3 px-3 py-3" : "justify-center p-3",
    "transition-all duration-150 ease-out",
    active
      ? theme.nav.item.active
      : clsx(
        theme.nav.item.base,
        isExpanded ? theme.nav.item.hoverExpanded : theme.nav.item.hoverCollapsed
      ),
    disabled && "opacity-50 cursor-not-allowed pointer-events-none",
    className
  );

  const content = (
    <>
      {/* Icon container - relative for collapsed badge positioning */}
      <span className={clsx(
        "shrink-0 size-[18px] [&>svg]:size-[18px]",
        !isExpanded && badge && "relative"
      )}>
        {icon}
        {/* Badge positioned top-right when collapsed */}
        {!isExpanded && badge && (
          <span className="absolute -top-1.5 -right-2.5">
            {badge}
          </span>
        )}
      </span>
      {isExpanded && (
        <motion.div
          className="flex items-center justify-between flex-1 min-w-0 overflow-hidden"
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: "auto" }}
          exit={{ opacity: 0, width: 0 }}
          transition={springConfig}
        >
          <span className="truncate whitespace-nowrap font-fono tracking-tighter">
            {label}
          </span>
          {badge && <span className="shrink-0 ml-2">{badge}</span>}
        </motion.div>
      )}
    </>
  );

  // External link
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={baseClasses}
        data-interactive="true"
      >
        {content}
      </a>
    );
  }

  // Disabled state
  if (disabled) {
    return <div className={baseClasses}>{content}</div>;
  }

  // Internal link
  return (
    <Link to={to} className={baseClasses} data-interactive="true">
      {content}
    </Link>
  );
}

/**
 * NavSpacer - Flexible spacer to push items to bottom
 */
export function NavSpacer({ className }) {
  return <div className={clsx("flex-1", className)} aria-hidden="true" />;
}

/**
 * NavDivider - Visual separator between sections
 */
export function NavDivider({ className }) {
  const { layout = "default" } = useLayout();
  const theme = getTheme(layout);

  return (
    <div
      className={clsx("my-3 h-px", theme.nav.divider, className)}
      aria-hidden="true"
    />
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { springConfig };
